
import { Component, Vue } from 'vue-property-decorator'
import { Form as ElForm } from 'element-ui/types/element-ui'

import { createMultiChoiceCardOrder } from '@/api/multiChoiceCardOrders'
import { extractApiErrorMessages } from '@/utils/apiErrors'
import { csvSubmitErrorMessage, mapPartialRowFailure, parseCsvSubmitError } from './utils/csvSubmitError'
import StepOrderType from './components/StepOrderType.vue'
import StepDotationNature from './components/StepDotationNature.vue'
import StepCardOptions from './components/StepCardOptions.vue'
import StepDistributionMode from './components/StepDistributionMode.vue'
import StepDotationEntry from './components/StepDotationEntry.vue'
import OrderSuccessDialog from './components/OrderSuccessDialog.vue'
import {
  createEmptyDotationOrderForm,
  DotationOrderForm,
  OrderResult
} from './types/dotationOrderForm'
import {
  buildCardOptionsRules,
  buildDistributionRules,
  buildEntryRules,
  buildNatureRules
} from './utils/validation'

@Component({
  name: 'NewDotationOrder',
  components: {
    StepOrderType,
    StepDotationNature,
    StepCardOptions,
    StepDistributionMode,
    StepDotationEntry,
    OrderSuccessDialog
  }
})
export default class extends Vue {
  form: DotationOrderForm = createEmptyDotationOrderForm()
  step = 0
  submitting = false
  successVisible = false
  orderResult: OrderResult = {
    reference: '',
    totalAmount: 0,
    successCount: 0,
    failsCount: 0,
    fails: []
  }

  get activeRules() {
    const t = this.$t.bind(this) as any
    if (this.step === 1) return buildNatureRules(t, () => this.form)
    if (this.step === 2) return buildCardOptionsRules(t, () => this.form)
    if (this.step === 3) return buildDistributionRules(t)
    if (this.step === 4) return buildEntryRules(t, () => this.form)
    return {}
  }

  get canSubmit(): boolean {
    if (!this.form.programId) return false
    if (this.form.nature === 'urssaf' && !this.form.ursafEventId) return false
    if (this.form.distributionMode === 'single') {
      return Boolean(this.form.customerId && this.form.amount)
    }
    if (this.form.distributionMode === 'csv') {
      return Boolean(this.form.csvBlob) && !this.form.csvHasErrors
    }
    return false
  }

  previousStep(): void {
    if (this.step > 0) this.step--
  }

  async nextStep(): Promise<void> {
    this.refreshCsvValidation()
    const valid = await this.validateForm()
    if (!valid) return
    if (this.step < 4) this.step++
  }

  validateForm(): Promise<boolean> {
    return new Promise(resolve => {
      (this.$refs.orderFormRef as ElForm).validate(valid => resolve(valid))
    })
  }

  private refreshCsvValidation(): void {
    const stepRef = this.$refs.stepDotationEntry as any
    stepRef?.refreshCsvValidation?.()
  }

  async submitOrder(): Promise<void> {
    this.refreshCsvValidation()
    const valid = await this.validateForm()
    if (!valid) return

    if (this.form.nature !== 'urssaf') {
      this.$notify({
        title: String(this.$t('newDotationOrder.notification.errorTitle')),
        message: String(this.$t('newDotationOrder.notification.classicUnavailable')),
        type: 'warning',
        duration: 6000
      })
      return
    }

    this.submitting = true
    try {
      const response = await createMultiChoiceCardOrder(this.buildPayload())
      const attributes = response?.data?.attributes || {}
      const success = attributes.success || []
      const fails = attributes.fails || []
      const amountCents = attributes.amount_cents || 0

      const t = this.$t.bind(this) as any
      const detailedFails = fails
        .filter((fail): fail is { row_number: number; errors?: string[] } => typeof fail.row_number === 'number')
        .map(fail => mapPartialRowFailure(fail, t))
        .filter((failure): failure is NonNullable<typeof failure> => Boolean(failure))

      this.orderResult = {
        reference: attributes.number || response?.data?.id || '',
        totalAmount: amountCents / 100,
        successCount: success.length,
        failsCount: fails.length,
        fails: detailedFails.map(failure => ({ rowNumber: failure.rowNumber, message: failure.message }))
      }
      this.successVisible = true

      if (detailedFails.length > 0) {
        this.applyPartialFailures(detailedFails)
        this.$notify({
          title: String(this.$t('newDotationOrder.notification.partialTitle')),
          message: String(this.$t('newDotationOrder.notification.partialMessage', {
            success: success.length,
            fails: fails.length
          })),
          type: 'warning',
          duration: 6000
        })
      }
    } catch (err: any) {
      this.handleSubmitError(err)
    } finally {
      this.submitting = false
    }
  }

  /** Highlights the row/field behind each line-level failure, for the "close the success dialog and look back" case. */
  private applyPartialFailures(failures: { rowNumber: number; field?: string; message: string }[]): void {
    const stepRef = this.$refs.stepDotationEntry as any
    failures.forEach(failure => {
      stepRef?.applyBackendRowError?.(failure.rowNumber, failure.field, failure.message)
    })
  }

  private handleSubmitError(err: any): void {
    const messages = extractApiErrorMessages(err)
    const csvError = this.form.distributionMode === 'csv'
      ? messages.map(parseCsvSubmitError).find((parsed): parsed is NonNullable<typeof parsed> => Boolean(parsed))
      : undefined

    if (csvError) {
      const t = this.$t.bind(this) as any
      const applied = (this.$refs.stepDotationEntry as any)?.applyBackendRowError?.(
        csvError.rowNumber, csvError.field, csvSubmitErrorMessage(csvError, t)
      )

      if (applied) {
        this.$notify({
          title: String(this.$t('newDotationOrder.notification.errorTitle')),
          message: String(this.$t('newDotationOrder.notification.csvRowError', {
            line: csvError.rowNumber,
            message: csvSubmitErrorMessage(csvError, t)
          })),
          type: 'error',
          duration: 8000
        })
        return
      }
    }

    this.$notify({
      title: String(this.$t('newDotationOrder.notification.errorTitle')),
      message: messages.join('\n') || String(this.$t('newDotationOrder.notification.errorGeneric')),
      type: 'error',
      duration: 8000
    })
  }

  /** 0 when the beneficiary funds the card out of their own dotation, otherwise each
   *  poste's own organization subsidy slider. */
  private cardChargeFields() {
    if (!this.form.enableBankCard) return {}

    const isOrganizationFunded = this.form.cardFunding === 'organization'
    const issuanceCharge = isOrganizationFunded ? this.form.organizationIssuanceSubsidyPercent / 100 : 0
    const loadingCharge = isOrganizationFunded ? this.form.organizationLoadingSubsidyPercent / 100 : 0

    return {
      caretaker_issuance_charge: issuanceCharge,
      caretaker_loading_charge: loadingCharge
    }
  }

  private buildPayload() {
    if (this.form.distributionMode === 'csv') {
      return {
        ursaf_event_id: this.form.ursafEventId,
        program_id: this.form.programId,
        type_request: 'csv' as const,
        blob: this.form.csvBlob,
        scheduled_at: this.form.scheduledAt,
        ...this.cardChargeFields()
      }
    }

    return {
      ursaf_event_id: this.form.ursafEventId,
      program_id: this.form.programId,
      type_request: 'input_fields' as const,
      customer_id: this.form.customerId,
      amount: String(this.form.amount),
      scheduled_at: this.form.scheduledAt,
      ...(this.form.customerClientReference
        ? { customer_client_reference: this.form.customerClientReference }
        : {}),
      ...this.cardChargeFields()
    }
  }

  handleSuccessClose(): void {
    this.successVisible = false
    this.$router.push('/gifts/orders')
  }
}
